// TipTap WYSIWYG editor for broadcast body. Storage shape is ProseMirror
// JSON — `value` is a `BroadcastDoc` (root `doc`, paragraph/heading/list
// nodes only, text marks bold/italic/underline/link). The editor's
// extension set is configured so it can ONLY produce shapes the server's
// `BroadcastDocSchema` accepts.
//
// Toolbar: 8 formatting buttons (B, I, U, link, bullet list, ordered list,
// H1, H2). Icons are imported directly from lucide-react — these are
// feature-specific glyphs (formatting controls), not the curated `I`
// design-system map (per `frontend-design`'s "Feature-specific glyphs →
// lucide-react inside the feature's own file" rule).

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import type { Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Heading1 as Heading1Icon,
  Heading2 as Heading2Icon,
  Image as ImageIcon,
  UserRound as UserRoundIcon,
} from 'lucide-react';
import { Row, VisuallyHidden } from '@leanin/ui';
import { Toggle as ToggleButton } from '@leanin/ui/client';
import {
  MERGE_TAG_LABELS,
  MERGE_TAG_NAMES,
  type MergeTagName,
} from '@leanin/shared/email/merge-tags';
import { type BroadcastDoc } from '@/lib/queries/broadcasts';
import {
  useFinalizeFeedPostMedia,
  usePresignFeedPostMedia,
} from '@/lib/mutations/feed';
import { uploadWithProgress } from '@/lib/upload';
import { MergeTag } from './MergeTag';
import { BroadcastImage } from './BroadcastImage';

import styles from './BroadcastBodyEditor.module.css';

// Image upload constraints, kept in lockstep with the server presign gate
// (posts/service/presign-post-media.ts): the same 12 MB cap and the image
// subset of its allowlist. The composer reuses the posts presign/finalize
// endpoints (as the DM composer does) rather than a third upload flow.
const IMAGE_UPLOAD_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const;
type ImageUploadType = (typeof IMAGE_UPLOAD_TYPES)[number];
const IMAGE_ACCEPT = IMAGE_UPLOAD_TYPES.join(',');
const MAX_IMAGE_BYTES = 12 * 1024 * 1024;

function isImageUploadType(t: string): t is ImageUploadType {
  return (IMAGE_UPLOAD_TYPES as readonly string[]).includes(t);
}

export type { BroadcastDoc };

// The full extension set, exported so tests can drive the exact document
// model the mounted editor uses (see MergeTag.test.ts). Everything here maps
// 1:1 onto a shape the server's `BroadcastDocSchema` accepts — including the
// `mergeTag` inline atom the email sender substitutes per recipient.
export function broadcastEditorExtensions(): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2] },
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      validate: (href) => /^(https?:|mailto:)/i.test(href),
    }),
    Placeholder.configure({ placeholder: 'Write your message…' }),
    MergeTag,
    BroadcastImage,
  ];
}

export interface BroadcastBodyEditorProps {
  readonly value: BroadcastDoc;
  readonly onChange: (doc: BroadcastDoc) => void;
  readonly disabled?: boolean;
}

export function BroadcastBodyEditor({
  value,
  onChange,
  disabled,
}: BroadcastBodyEditorProps) {
  const editor = useEditor({
    extensions: broadcastEditorExtensions(),
    content: value,
    editable: !disabled,
    // Fires only for doc-changing transactions, so every call is member
    // input: installing `content` at creation dispatches no update, and the
    // enable-state sync below opts out of its own.
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getJSON() as BroadcastDoc);
    },
    editorProps: {
      attributes: {
        class: styles.canvas ?? '',
      },
    },
  });

  // Keep editor enable-state in sync with the disabled prop. The initial
  // value is set above; we don't push subsequent `value` updates back in to
  // avoid clobbering the editor's own caret/selection state on every
  // keystroke (re-render loop). The parent owns the doc; the editor is the
  // canonical writer while mounted.
  useEffect(() => {
    if (editor === null) return;
    editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  if (editor === null) {
    return <div className={styles.shell} aria-busy="true" />;
  }

  return (
    <div className={styles.shell}>
      <Toolbar editor={editor} disabled={disabled === true} />
      <EditorContent editor={editor} className={styles.content} />
    </div>
  );
}

type ToolbarProps = {
  readonly editor: Editor;
  readonly disabled: boolean;
};

function Toolbar({ editor, disabled }: ToolbarProps) {
  return (
    <Row
      gap="xs"
      align="center"
      wrap
      className={styles.toolbar}
      role="toolbar"
      aria-label="Formatting"
    >
      <ToolbarButton
        label="Bold"
        active={editor.isActive('bold')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive('italic')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        active={editor.isActive('underline')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Link"
        active={editor.isActive('link')}
        disabled={disabled}
        onClick={() => promptForLink(editor)}
      >
        <LinkIcon size={16} strokeWidth={2} />
      </ToolbarButton>

      <span className={styles.divider} aria-hidden="true" />

      <ToolbarButton
        label="Bullet list"
        active={editor.isActive('bulletList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive('orderedList')}
        disabled={disabled}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrderedIcon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ImageButton editor={editor} disabled={disabled} />

      <span className={styles.divider} aria-hidden="true" />

      <ToolbarButton
        label="Heading 1"
        active={editor.isActive('heading', { level: 1 })}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 1 }).run()
        }
      >
        <Heading1Icon size={16} strokeWidth={2} />
      </ToolbarButton>
      <ToolbarButton
        label="Heading 2"
        active={editor.isActive('heading', { level: 2 })}
        disabled={disabled}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
      >
        <Heading2Icon size={16} strokeWidth={2} />
      </ToolbarButton>

      <span className={styles.divider} aria-hidden="true" />

      {MERGE_TAG_NAMES.map((name) => (
        <MergeTagButton
          key={name}
          name={name}
          editor={editor}
          disabled={disabled}
        />
      ))}
    </Row>
  );
}

// Inserts the merge-tag chip at the cursor, followed by a space so typing
// continues naturally after it. Without this button a leader who deletes
// the prefilled chip has no way to get personalization back.
function MergeTagButton({
  name,
  editor,
  disabled,
}: {
  readonly name: MergeTagName;
  readonly editor: Editor;
  readonly disabled: boolean;
}) {
  const label = MERGE_TAG_LABELS[name];
  return (
    <ToolbarButton
      label={`Insert ${label.toLowerCase()}`}
      active={editor.isActive('mergeTag', { name })}
      disabled={disabled}
      wide
      onClick={() =>
        editor
          .chain()
          .focus()
          .insertContent([
            { type: 'mergeTag', attrs: { name } },
            { type: 'text', text: ' ' },
          ])
          .run()
      }
    >
      <UserRoundIcon size={16} strokeWidth={2} />
      <span aria-hidden="true">{label}</span>
    </ToolbarButton>
  );
}

// Uploads an image and inserts it at the caret. Reuses the posts
// presign/finalize endpoints (the same ones the feed + DM composers use) —
// no third upload flow — then inserts a `{ type: 'image', attrs }` node whose
// `key` is the finalize step's processed-media key. Alt text is captured with
// a `window.prompt`, matching the link button's lightweight entry pattern.
function ImageButton({
  editor,
  disabled,
}: {
  readonly editor: Editor;
  readonly disabled: boolean;
}) {
  const presign = usePresignFeedPostMedia();
  const finalize = useFinalizeFeedPostMedia();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const uploadAndInsert = async (file: File) => {
    if (!isImageUploadType(file.type)) {
      window.alert('Choose a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      window.alert('That image is larger than 12 MB. Choose a smaller file.');
      return;
    }
    const contentType = file.type;
    const altInput = window.prompt(
      'Describe this image (alt text for screen readers)',
      '',
    );
    if (altInput === null) return; // user cancelled the insert
    const alt = altInput.trim();

    setBusy(true);
    try {
      const presigned = await presign.mutateAsync({
        contentType,
        sizeBytes: file.size,
      });
      await uploadWithProgress({
        url: presigned.uploadUrl,
        body: file,
        headers: presigned.fields,
        onProgress: () => {},
      });
      const processed = await finalize.mutateAsync({
        key: presigned.key,
        contentType,
        sizeBytes: file.size,
      });
      if (processed.kind !== 'image') {
        window.alert('That file is not an image.');
        return;
      }
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'image',
          attrs: { key: processed.key, alt, width: processed.width },
        })
        .run();
    } catch {
      window.alert('That image could not be uploaded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className={styles.fileInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset so picking the same file again re-fires onChange.
          e.target.value = '';
          if (file) void uploadAndInsert(file);
        }}
      />
      <ToolbarButton
        label="Insert image"
        active={false}
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
      >
        <ImageIcon size={16} strokeWidth={2} />
      </ToolbarButton>
    </>
  );
}

type ToolbarButtonProps = {
  readonly label: string;
  readonly active: boolean;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
  // Sized to its icon + text content instead of the square 28px icon slot —
  // used by the labeled merge-tag button.
  readonly wide?: boolean;
};

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children,
  wide = false,
}: ToolbarButtonProps) {
  return (
    <ToggleButton
      pressed={active}
      onPressedChange={onClick}
      disabled={disabled}
      aria-label={label}
      className={wide ? `${styles.btn} ${styles.btnWide}` : styles.btn}
      onMouseDown={(e) => {
        // Prevent the toolbar button from stealing focus from the editor —
        // otherwise the caret jumps and the formatting command runs against
        // an empty selection.
        e.preventDefault();
      }}
    >
      {children}
      <VisuallyHidden>{label}</VisuallyHidden>
    </ToggleButton>
  );
}

function promptForLink(editor: Editor) {
  // `window.prompt` is the lightest possible link-entry UI; a richer popover
  // can replace this later (the editor API stays the same — set/extend a
  // link mark on the current selection).
  const previous = (editor.getAttributes('link')['href'] as string | undefined) ?? '';
  const next = window.prompt('Link URL (https:// or mailto:)', previous);
  if (next === null) return; // user cancelled
  const trimmed = next.trim();
  if (trimmed.length === 0) {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  if (!/^(https?:|mailto:)/i.test(trimmed)) {
    window.alert('Link must start with http://, https://, or mailto:');
    return;
  }
  editor
    .chain()
    .focus()
    .extendMarkRange('link')
    .setLink({ href: trimmed })
    .run();
}

// Helper used by other consumers (composer subject field) to ensure the
// editor has a non-empty starting doc. Returning a one-paragraph empty doc
// matches what TipTap produces when the user has typed nothing, so the
// editor's initial state and "user typed nothing" round-trip identically.
export function emptyBroadcastDoc(): BroadcastDoc {
  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: [] }],
  } as BroadcastDoc;
}

